import { useContext, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import AuthContext from "@/shared/context/auth-context";
import { getData } from "@/shared/utils/api";
import { IObject } from "@/shared/utils/utils";
import SearchInput from "@/manager/components/search-input/search-input";
import { useHttpGet } from "@/shared/hooks/use-httpNonTable";
import Collapsible from "@/manager/components/collapsible/collapsible";
import { IDatamodelRelation } from "./referencing-component-tab";
import { IEnumerationOption } from "@/shared/definitions/datatypes";
import { BsTrash } from "react-icons/bs";

interface IReferencingComponentConfigurationProps {
  onRemove: (id: string) => void;
  onUpdate: (update: IDatamodelRelation) => void;
  currentDatamodelFields: {
    id: string;
    field_name: string;
    field_datatype: string;
  }[];
  datamodels: {
    id: string;
    name: string;
    type: string;
  }[];
  datamodelRelation: IDatamodelRelation;
}

interface IDatamodelData {
  fields: {
    id: string;
    field_name: string;
    field_datatype: string;
  }[];
}

const ReferencingComponentConfiguration = ({
  onRemove,
  onUpdate,
  datamodels,
  currentDatamodelFields,
  datamodelRelation
}: IReferencingComponentConfigurationProps) => {
  const authCtx = useContext(AuthContext);
  
  const [targetDatamodel, setTargetDatamodel] = useState<IDatamodelData | undefined>(undefined);

  const { sendRequest: getRecord, status: getRecordStatus, data: getRecordData, error: getRecordError } = useHttpGet(
    getData, 
    true, 
    authCtx.token, 
    "default", 
    "platform"
  );

  useEffect(() => {
    if (datamodelRelation.target_datamodel_id && datamodelRelation.target_type) {
      fetchDatamodelFields(datamodelRelation.target_datamodel_id, datamodelRelation.target_type);
    }
  }, []);

  useEffect(() => {
    if (getRecordStatus === "completed" && !getRecordError && getRecordData?.fields) {
      setTargetDatamodel({ fields: getRecordData.fields });

      if (datamodelRelation.relations.length > 0) {
        // Already have relations defined, no need to auto-populate
        return;
      }

      const pks = getPrimaryKeyFields();
      if (pks.length === 0) {
        return;
      }
      onUpdate({
        ...datamodelRelation,
        relations: pks.map(pk => ({
          source_field_id: "",
          target_field_id: pk.id
        }))
      });
    }
  }, [getRecordData, getRecordStatus, getRecordError]);

  const fetchDatamodelFields = (datamodelId: string, datamodelType: string) => {    
    if (datamodelType.toLowerCase().includes("entity")) {
      getRecord(`datamodel/entity/${datamodelId}`);
    } else if (datamodelType.toLowerCase().includes("event")) {
      getRecord(`datamodel/event/${datamodelId}`);
    } else if (datamodelType.toLowerCase().includes("alertcase")) {
      getRecord(`datamodel/alertcase/${datamodelId}`);
    }
  };

  const getPrimaryKeyFields = () => {
    if (!targetDatamodel?.fields) {
      return [];
    }
    
    return targetDatamodel.fields
      .filter((field: IObject) => field.primary_key)
      .map((field: IObject) => ({
        id: field.id,
        field_name: field.field_name,
        input_field_id: field.input_field_id,
      }));
  };

  const primaryKeyFields = getPrimaryKeyFields();

  const handleDatamodelChange = (selectedOption: IEnumerationOption | undefined) => {
    const targetDatamodelType = datamodels.find(dm => dm.id === selectedOption?.id)?.type || "";
    onUpdate({
      ...datamodelRelation,
      target_datamodel_id: selectedOption?.id || "", 
      target_type: targetDatamodelType,
      relations: []
    });

    if (selectedOption?.id && selectedOption?.name) {
      fetchDatamodelFields(selectedOption.id, selectedOption.name);
    }
  };

  const handleRelationChange = (relation_id: string, newValue: IEnumerationOption | undefined) => {
    const existingRelationIndex = datamodelRelation.relations.findIndex(
      relation => relation.target_field_id === relation_id
    );

    let updatedRelations;
    
    if (existingRelationIndex !== -1) {
      updatedRelations = datamodelRelation.relations.map(relation => {
        if (relation.target_field_id === relation_id) {
          return {
            ...relation,
            source_field_id: newValue?.id || "",
          };
        }
        return relation;
      });
    } else {
      updatedRelations = [
        ...datamodelRelation.relations,
        {
          source_field_id: newValue?.id || "",
          target_field_id: relation_id
        }
      ];
    }

    onUpdate({
      ...datamodelRelation,
      relations: updatedRelations
    });
  };

  return <div
      className="referencingComponentConfiguration"
      key={datamodelRelation.id}
    >
      <Collapsible 
          title={
            datamodelRelation.target_datamodel_id 
            ? `${getRecordData?.name || "Referencing Component"} (${datamodelRelation.relations
            .map(relation => {
              const field = currentDatamodelFields.find(field => field.id === relation.source_field_id);
              return field?.field_name;
            })
            .filter(name => !!name)
            .join(", ")})`
              : "New referencing component"
          }
          defaultOpen={!datamodelRelation.target_datamodel_id}
        >
        <SearchInput
          label={"Datamodel"}
          handleOnChange={handleDatamodelChange}
          data={datamodels?.map(
            (row: IObject) => ({ 
          id: row.id, 
          name: `${row?.name} (${row.type.charAt(0).toUpperCase() + row.type.slice(1)})` 
            })) || []}
          selectedOption={datamodelRelation.target_datamodel_id || ""}
          inputKey={`Datamodel_${datamodelRelation.id}`}
          required={true}
        />
          <div className="referencingComponentConfiguration-pkMappingSection">
            {primaryKeyFields.map((pkField: IObject) => {
              const selectedOption = datamodelRelation.relations.find(relation => relation.target_field_id === pkField.id);
              const referencingFieldId = pkField.id || "";
              const targetField = targetDatamodel?.fields.find(f => f.id === pkField.id);
              const filteredFields = currentDatamodelFields?.filter((field: IObject) => 
            field.field_datatype === targetField?.field_datatype
              ) || [];
              
              return (
                <SearchInput
                  key={`${datamodelRelation.id}_${referencingFieldId}`}
                  label={`Referencing field to primary key ${pkField.field_name}`}
                  handleOnChange={(newValue) => handleRelationChange(pkField.id, newValue) }
                  data={
                    filteredFields.map((field: IObject) => ({
                  id: field.id,
                  name: field.field_name,
                    }))
                  }
                  selectedOption={selectedOption?.source_field_id || ""}
                  required={true} 
                  inputKey={`Referencing_field_${datamodelRelation.id}_${referencingFieldId}`}    
                />
              );
            })}
    </div>
        {targetDatamodel && primaryKeyFields.length === 0 && (
          <div className="referencingComponentConfiguration-noPkMessage">
            No primary key fields found for the selected datamodel.
          </div>
        )}
        <Button 
          className="referencingComponentConfiguration-removeButton"
          variant="secondary" 
          onClick={() => onRemove(datamodelRelation.id)}
        >
          <BsTrash/>
        </Button>
      </Collapsible>
    </div>;
};

export default ReferencingComponentConfiguration;
